import React, { useEffect, useState } from "react";
import { Card, Button } from "../components/ui";
import { MockAPI } from "../services/api";
import { useFarm } from "../services/farmContext";
import { useAuth } from "../services/authContext";
import { Farm } from "../types";
import { useSettingsChanges } from "../services/settingsChanges";
type Draft = {
  name: string;
  llc_name: string;
  address: string;
  usda_farm_number: string;
  county: string;
  state: string;
};
const fields = [
  ["name", "Farm name"],
  ["llc_name", "LLC / legal name"],
  ["address", "Address"],
  ["usda_farm_number", "USDA farm number"],
  ["county", "County"],
  ["state", "State"],
] as const;
const draftOf = (farm: Farm): Draft =>
  Object.fromEntries(fields.map(([key]) => [key, farm[key] || ""])) as Draft;
export default function Settings() {
  const { activeFarm } = useFarm();
  const { privileges } = useAuth();
  const [draft, setDraft] = useState<Draft | null>(null),
    [baseline, setBaseline] = useState(""),
    [farmId, setFarmId] = useState("");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [message, setMessage] = useState("");
  const dirty = !!draft && JSON.stringify(draft) !== baseline;
  useSettingsChanges(dirty);
  useEffect(() => {
    if (activeFarm && (activeFarm.farm_id !== farmId || !dirty)) {
      const value = draftOf(activeFarm);
      setDraft(value);
      setBaseline(JSON.stringify(value));
      setFarmId(activeFarm.farm_id);
    }
  }, [activeFarm, farmId, dirty]);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeFarm || !draft || activeFarm.farm_id !== farmId) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const clean = {
        ...draft,
        name: draft.name.trim(),
        state: draft.state.trim().toUpperCase(),
      };
      await MockAPI.saveFarm({ ...activeFarm, ...clean });
      setDraft(clean);
      setBaseline(JSON.stringify(clean));
      setMessage("Farm profile saved.");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not save the farm profile.",
      );
    } finally {
      setBusy(false);
    }
  };
  if (!draft)
    return (
      <p role="status" className="text-gray-500">
        Loading farm profile…
      </p>
    );
  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Farm profile</h2>
        <p className="text-sm text-gray-500 mt-1">
          The name and address used throughout your workspace.
        </p>
      </div>
      <Card className="p-5 sm:p-6">
        <form onSubmit={save} className="space-y-6">
          <fieldset
            disabled={busy || !privileges.settings?.write}
            className="grid sm:grid-cols-2 gap-5"
          >
            {fields.map(([key, label]) => (
              <label
                key={key}
                className={`text-sm font-medium text-gray-700 ${key === "address" ? "sm:col-span-2" : ""}`}
              >
                {label}
                <input
                  required={key === "name"}
                  maxLength={key === "state" ? 2 : 200}
                  value={draft[key]}
                  onChange={(e) => {
                    setDraft({ ...draft, [key]: e.target.value });
                    setMessage("");
                  }}
                  className="mt-1.5 w-full border rounded-lg p-3 font-normal"
                />
              </label>
            ))}
          </fieldset>
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <div className="flex items-center justify-between gap-3 border-t pt-4">
            <p role="status" className="text-sm text-usda-700">
              {message || (dirty ? "Unsaved changes" : "")}
            </p>
            <Button
              type="submit"
              disabled={
                !dirty ||
                busy ||
                !draft.name.trim() ||
                !privileges.settings?.write
              }
            >
              {busy ? "Saving…" : "Save profile"}
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}
